/* ══════════════════════════════════════════════════════════════════════════
 * games.js — WHAT A GAME IS, FOR THE REACT APP.
 *
 * ⚠️⚠️⚠️ WHY THIS FILE EXISTS, AND IT IS NOT A NEW IDEA.
 * It is `Eleater Prototype/eleat_games.js` and `eleat_gd_days.js`, ported
 * field for field. Those two files are the locked Eleater venue screen's own
 * owners of a game and of the week strip, and their headers carry Al's
 * instructions from 12, 13 and 15 September 2026. Nothing here is invented.
 *
 * ⚠️ WHAT WENT WRONG AND WHY THIS IS BEING WRITTEN IN LATE SEPTEMBER.
 * `11_Restaurant.html` has held the whole Eleater Game Day design since
 * mid-September — the game, the sport strip, the week, the day, the phases,
 * the menu window computed from the kick-off, and "a day with no game has no
 * Game Day menu". **React's venue page was built without any of it** and drew
 * the Game Day deals as a flat list. Al, 25 September 2026: *"the eleater
 * never sees a game at all… That's exactly what we've been wanting to do.
 * Why do you not understand this? It's in the fucking schema as well."*
 * He is right. It is in 019 and it is on his locked screen, and I dropped it.
 *
 * ⚠️ EVERY WORD AN ELEATER READS IS BUILT, NEVER STORED BESIDE THE FACTS.
 * `label`, `title`, `whenText`, `windowOf` — all computed, so a label can
 * never disagree with the record it sits next to.
 *
 * ⚠️ A DATE, NEVER A WEEKDAY. `sport_events.starts_at` and
 * `game_day_games.starts_at` are `timestamptz`. A weekday cannot say WHICH
 * Saturday, cannot sort across a week, cannot roll over at midnight, and
 * cannot render a Lisbon kick-off in Hong Kong time.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️ THE WINDOW OFFSETS ARE THE VENUE'S, NOT CONSTANTS HERE.
   `game_day_menus.before_lead_min` / `after_trail_min`, 120 each by default
   (019). These are the fallback for when the server has not said — never a
   value that overrides what it did say. */
export const BEFORE_MIN = 120;
export const AFTER_MIN = 120;

const SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FULLMON = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

const pad = (n) => (n < 10 ? '0' : '') + n;

/* ══════════════════════════════════════════════════════════════════════════
 * THE ONE PLACE A `timestamptz` BECOMES A LOCAL DAY AND A LOCAL TIME.
 *
 * ⚠️ THE SERVER SENDS AN INSTANT AND THE SCREEN SHOWS A WALL CLOCK. 019:
 * *"A La Liga game at 8pm Lisbon shows as 12:00 PM in Calgary and 3:00 AM in
 * Hong Kong, automatically, including the correct local DAY when the game
 * crosses midnight."*  So the day key is derived from the same Date object
 * the time is, and never from slicing the ISO string — `"…T23:30:00Z"`
 * sliced at ten characters gives the WRONG DAY for half the planet.
 * ══════════════════════════════════════════════════════════════════════════ */
/* ⚠️⚠️ THE VENUE'S WALL CLOCK, NOT THE PHONE'S — 2 October 2026.
   Found walking the app: The Unicorn (Calgary) viewed from Hong Kong printed
   an NHL game at "Fri 7:00 AM" with the menu "5:00 AM – 11:40 AM", while the
   card on Home — `badgeFor()`, fixed to `restaurants.timezone` on 1 October —
   said TODAY 5 PM. Two answers to one kick-off on two screens. The game is ON
   at the venue, at the venue's hour, which is 019's own example: *"shows as
   12:00 PM in Calgary"* — the venue in Calgary.
   So every instant here is first turned into the venue's wall clock: a Date
   whose local fields ARE the venue's year, month, day, hour and minute. Every
   function below then reads it exactly as before. `setZone()` is called by
   the pane with `restaurants.timezone`; with none, the phone's clock is all
   there is, which is what shipped before. */
let ZONE = null;
export function setZone(tz) { ZONE = tz || null; }
/* 151 — the venue's own clock, 12h ("7:00 PM") or 24h ("19:00"), chosen on
   its Hours screen (Al, 20 Sep 2026) */
let CLOCK = '12h';
export function setClock(f) { CLOCK = f === '24h' ? '24h' : '12h'; }

export function wall(d) {
  if (!d || Number.isNaN(d.getTime())) return null;
  if (!ZONE) return d;
  try {
    const p = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: ZONE, hourCycle: 'h23', year: 'numeric', month: 'numeric',
      day: 'numeric', hour: 'numeric', minute: 'numeric',
    }).formatToParts(d).forEach((x) => { p[x.type] = x.value; });
    return new Date(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute);
  } catch (e) { return d; }
}

/* now, on the venue's clock — what "today" and "this week" mean on its page */
export function now() { return wall(new Date()); }

export function localDay(iso) {
  const d = wall(iso ? new Date(iso) : null);
  if (!d) return '';
  return ymd(d);
}

export function localMinutes(iso) {
  const d = wall(iso ? new Date(iso) : null);
  if (!d) return null;
  return d.getHours() * 60 + d.getMinutes();
}

export function ymd(dt) {
  return dt.getFullYear() + '-' + pad(dt.getMonth() + 1) + '-' + pad(dt.getDate());
}

/* ⚠️ A LOCAL DATE, NEVER UTC. `new Date('2026-09-19')` is parsed as UTC and
   lands on the 18th in the Americas — a game on Saturday must not show up on
   Friday. From `eleat_gd_days.js`, and the reason is quoted there. */
export function dateOf(key) {
  if (!key) return null;
  const p = String(key).split('-');
  if (p.length !== 3) return null;
  const d = new Date(+p[0], +p[1] - 1, +p[2]);
  return Number.isNaN(d.getTime()) ? null : d;
}

/* minutes since midnight → '7:00 PM'. The admin screen's own f12(). */
export function f12(total) {
  if (total === null || total === undefined || Number.isNaN(Number(total))) return '';
  const t = ((Number(total) % 1440) + 1440) % 1440;
  let h = Math.floor(t / 60);
  const mn = pad(t % 60);
  if (CLOCK === '24h') return pad(h) + ':' + mn;
  const ap = h >= 12 ? 'PM' : 'AM';
  h %= 12; if (h === 0) h = 12;
  return h + ':' + mn + ' ' + ap;
}

export function dayShort(key) { const d = dateOf(key); return d ? SHORT[d.getDay()] : ''; }

export function dayLong(key) {
  const d = dateOf(key);
  if (!d) return '';
  try {
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  } catch (e) { return String(key); }
}

/* ══════════════════════════════════════════════════════════════════════════
 * WHAT THE GAME IS CALLED.
 *
 * ⚠️ `event_name` CARRIES THE ONE-SIDED EVENTS and that is why it exists. A
 * fight, a race, a tournament round and a race meeting have no two sides.
 * 019 gives the table three fields for this; one string could not tell a
 * title fight from a derby.
 * ══════════════════════════════════════════════════════════════════════════ */
export function title(g) {
  if (!g) return '';
  const a = g.team_home || g.teamA || '';
  const b = g.team_away || g.teamB || '';
  if (a && b) return a + ' v ' + b;
  return g.event_name || g.name || a || b || '';
}

export function whenText(g) {
  if (!g) return '';
  const d = dayShort(localDay(g.starts_at));
  const t = f12(localMinutes(g.starts_at));
  return d && t ? d + ' ' + t : (d || t);
}

export function label(g) {
  const a = title(g); const b = whenText(g);
  return b ? a + ' · ' + b : a;
}

/* ══════════════════════════════════════════════════════════════════════════
 * THE MENU WINDOW — DERIVED FROM THE KICK-OFF, NEVER STORED AS CLOCK TIMES.
 *
 * 019, verbatim: *"before = starts_at - menu.before_lead_min → starts_at;
 * during = starts_at → starts_at + duration_min; after = +duration_min →
 * +duration_min + menu.after_trail_min."*
 *
 * ⚠️ AND RESTAURANT ADMIN ALREADY PRINTS THIS under every game — *"menu live
 * 6:00 PM – 12:00 AM"*. An Eleater looking at the same game gets the same
 * fact, worked out the same way, which is the whole reason it is one function.
 * ══════════════════════════════════════════════════════════════════════════ */
export function windowOf(g, menu) {
  const k = localMinutes(g && g.starts_at);
  if (k === null) return null;
  const lead = Number.isFinite(Number(menu && menu.before_lead_min))
    ? Number(menu.before_lead_min) : BEFORE_MIN;
  const trail = Number.isFinite(Number(menu && menu.after_trail_min))
    ? Number(menu.after_trail_min) : AFTER_MIN;
  const dur = Number.isFinite(Number(g && g.duration_min)) ? Number(g.duration_min) : 180;
  return {
    fromMin: k - lead,
    toMin: k + dur + trail,
    fromText: f12(k - lead),
    toText: f12(k + dur + trail),
  };
}

/* ⚠️ WHICH PHASE A MOMENT FALLS IN. Used for "on now" and for landing the
   Live badge on the game that is actually playing. Returns null OUTSIDE the
   whole window — a badge that lands on nothing is worse than no badge. */
export function phaseAt(g, menu, atMin) {
  const k = localMinutes(g && g.starts_at);
  const w = windowOf(g, menu);
  if (k === null || !w) return null;
  const dur = Number.isFinite(Number(g && g.duration_min)) ? Number(g.duration_min) : 180;
  const m = Number(atMin);
  if (m < w.fromMin || m > w.toMin) return null;
  if (m < k) return 'before';
  if (m <= k + dur) return 'during';
  return 'after';
}

/* ⚠️ A GAME SWITCHED OFF IS NOT A DELETED GAME. `is_live === false` is the
   venue saying a screen got given to another match: it comes off Eleat and
   its schedule and the menu are untouched. Anything other than an explicit
   false is live, which is what Restaurant Admin does. */
export function isLive(g) { return !!g && g.is_live !== false && g.live !== false; }

export function byStart(a, b) {
  return String(a && a.starts_at || '').localeCompare(String(b && b.starts_at || ''));
}

/* ── the week strip ─────────────────────────────────────────────────────────
   ⚠️ MONDAY TO SUNDAY, MATCHING THE RESTAURANT ADMIN BUILDER. `getDay()`
   calls Sunday 0, so Sunday goes BACK six days, not forward one. */
export function mondayOf(dt) {
  const x = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  const dow = x.getDay();
  x.setDate(x.getDate() - (dow === 0 ? 6 : dow - 1));
  return x;
}

export function week(games, offset) {
  const start = mondayOf(now());
  start.setDate(start.getDate() + (offset || 0) * 7);
  const today = ymd(now());
  const out = [];
  for (let i = 0; i < 7; i += 1) {
    const dt = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const key = ymd(dt);
    const mine = (games || []).filter((g) => g && localDay(g.starts_at) === key);
    out.push({
      key,
      short: SHORT[dt.getDay()],
      num: dt.getDate(),
      label: SHORT[dt.getDay()] + ' ' + dt.getDate() + ' ' + MON[dt.getMonth()],
      isToday: key === today,
      isPast: key < today,
      games: mine,
      /* ⚠️ NEVER "3 left" — a count remaining is banned. This is how many
         games are ON, which is a different thing. */
      count: mine.length,
    });
  }
  return out;
}

/* ⚠️ WHICH DAY THE STRIP LANDS ON — `gdLandOn()` in `11_Restaurant.html`,
   ported. Today if there is a game today, otherwise the next day there is
   one, never the past; an empty week lands on its Monday. Used on opening,
   on choosing a sport and on stepping a week, as the locked screen does —
   React landed on today every time, so choosing UFC / MMA on a Thursday
   said "No games" while the fight sat on Saturday, unmarked. (Found walking
   the app, 2 October 2026.) Al, 15 Sep: *"so they can click that and see
   which day it's playing."* */
export function landOn(games, offset) {
  const days = week((games || []).filter(isLive), offset);
  const hit = days.filter((d) => d.count && !d.isPast)[0]
    || days.filter((d) => d.count)[0];
  return (hit || days[0]).key;
}

/* ⚠️ THE MONTH LIVES HERE, ONCE — NOT ON ALL SEVEN CHIPS. Al, 15 September
   2026: *"can we have, like, Monday to Sunday, then underneath it, Monday the
   seventeenth, and then June in very smaller letters"*. Seven copies of one
   fact is seven things to get wrong — the same reasoning that killed
   `var(--cream, #F5E4D2)`. And a week can sit in two months, so this says
   "Sep – Oct" rather than picking one and being wrong for four days. */
export function weekLabel(offset) {
  const start = mondayOf(now());
  start.setDate(start.getDate() + (offset || 0) * 7);
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
  const months = start.getMonth() === end.getMonth()
    ? FULLMON[start.getMonth()]
    : MON[start.getMonth()] + ' – ' + MON[end.getMonth()];
  if (!offset) return 'This week · ' + months;
  if (offset === 1) return 'Next week · ' + months;
  if (offset === -1) return 'Last week · ' + months;
  return MON[start.getMonth()] + ' ' + start.getDate() + ' – '
    + MON[end.getMonth()] + ' ' + end.getDate();
}

/* ⚠️ SO THE ARROW STOPS WHERE THE GAMES STOP. An arrow that keeps going into
   eight empty weeks is a control that lies about what is behind it. */
export function weeksAhead(games) {
  const m = mondayOf(now());
  let far = 0;
  (games || []).forEach((g) => {
    const dt = dateOf(localDay(g && g.starts_at));
    if (!dt) return;
    const wk = Math.round((mondayOf(dt) - m) / 604800000);
    if (wk > far) far = wk;
  });
  return far;
}

export function weeksBehind(games) {
  const m = mondayOf(now());
  let back = 0;
  (games || []).forEach((g) => {
    const dt = dateOf(localDay(g && g.starts_at));
    if (!dt) return;
    const wk = Math.round((mondayOf(dt) - m) / 604800000);
    if (wk < back) back = wk;
  });
  return back;
}

/* ── the sports this venue actually has a game in ───────────────────────────
   ⚠️ A TAB FOR A SPORT WITH NOTHING BEHIND IT IS A PROMISE ELEAT CANNOT KEEP.
   Al, 15 September 2026: *"MMA could be in the same evening as a Monday night
   football game or a soccer game or a golf match… it should show what is
   showing in the different categories of sports."*  `All sports` first, then
   only the sports present, in the order their first game starts. */
export function sportsIn(games) {
  const seen = [];
  (games || []).slice().sort(byStart).forEach((g) => {
    const s = (g && g.sport) || '';
    if (s && seen.indexOf(s) < 0) seen.push(s);
  });
  return seen;
}

export function gamesOn(games, dayKey, sport) {
  return (games || [])
    .filter((g) => isLive(g) && localDay(g.starts_at) === dayKey)
    .filter((g) => !sport || sport === 'all' || g.sport === sport)
    .sort(byStart);
}
